import { useState } from 'react'
import { ChevronDown, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { inlineEditKey, inlineTextareaKey } from '@/components/inline-edit-keys'
import { useCloseAfterPress } from '@/components/use-close-after-press'

export type POInlineEditKind =
  | 'text'
  | 'money'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'textarea'

// An option with this value clears the field.
export const EMPTY_SELECT = '__unset__'

type POInlineEditOption = {
  value: string
  label: string
}

/**
 * Draft quote fields close immediately after confirmation. Placed PO cells and
 * invoice summary fields have different save/rollback lifecycles; only their
 * keyboard dispatch is shared with this control.
 */
export function POInlineEditableField({
  value,
  displayValue,
  placeholder = '-',
  kind = 'text',
  disabled,
  align = 'left',
  inputClassName,
  buttonClassName,
  options,
  title,
  ariaLabel,
  pending = false,
  invalid = false,
  showEditIcon = false,
  allowWrap = false,
  onEditingChange,
  onConfirm,
}: {
  value: unknown
  displayValue?: string
  placeholder?: string
  kind?: POInlineEditKind
  disabled?: boolean
  align?: 'left' | 'right'
  inputClassName?: string
  buttonClassName?: string
  options?: POInlineEditOption[]
  title?: string
  ariaLabel?: string
  pending?: boolean
  invalid?: boolean
  showEditIcon?: boolean
  allowWrap?: boolean
  onEditingChange?: (editing: boolean) => void
  onConfirm: (rawValue: string) => void
}) {
  const initial = valueToInput(value)
  const [editing, setEditing] = useState(false)
  const [current, setCurrent] = useState(initial)

  const setEditorOpen = (open: boolean) => {
    setEditing(open)
    onEditingChange?.(open)
  }

  const commit = (nextValue = current) => {
    if (disabled || pending) return
    if (nextValue === initial) {
      setEditorOpen(false)
      return
    }
    onConfirm(nextValue)
    setEditorOpen(false)
  }

  const cancel = () => {
    setCurrent(initial)
    setEditorOpen(false)
  }

  // A box blurred by a press saves now and closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing && kind !== 'boolean' && kind !== 'select')
  const commitOnBlur = (nextValue: string) => {
    if (disabled || pending) return
    if (nextValue !== initial) onConfirm(nextValue)
    closeAfterPress(() => setEditorOpen(false))
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel ?? title ?? 'Edit value'}
        aria-busy={pending}
        aria-disabled={disabled || pending || undefined}
        aria-invalid={invalid || undefined}
        onClick={() => {
          if (pending) return
          setCurrent(initial)
          setEditorOpen(true)
        }}
        title={title ?? 'Click to edit'}
        className={cn(
          'group/inline-edit inline-flex min-h-8 min-w-24 max-w-full items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent',
          invalid && 'border border-destructive/45 bg-destructive/5 hover:bg-destructive/10',
          pending && 'cursor-progress',
          align === 'right' && '-mr-1.5 tabular-nums',
          buttonClassName,
        )}
      >
        <span className={cn('min-w-0', allowWrap ? 'whitespace-pre-wrap text-left' : 'truncate')}>
          {(displayValue ?? initial) || placeholder}
        </span>
        {kind === 'select' ? (
          <ChevronDown className="size-3 shrink-0 opacity-60" aria-hidden="true" />
        ) : showEditIcon && !pending ? (
          <Pencil className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/inline-edit:opacity-100 group-focus-visible/inline-edit:opacity-100" aria-hidden="true" />
        ) : null}
      </button>
    )
  }

  const editor =
    kind === 'boolean' || kind === 'select' ? (
      <Select
        open={editing}
        value={current || EMPTY_SELECT}
        onOpenChange={(open) => {
          if (!open) cancel()
        }}
        onValueChange={(next) => {
          const value = next === EMPTY_SELECT ? '' : next
          setCurrent(value)
          commit(value)
        }}
        disabled={disabled || pending}
      >
        <SelectTrigger className={cn('h-7 min-w-24 text-sm', inputClassName)} autoFocus>
          <SelectValue placeholder="Choose" />
        </SelectTrigger>
        <SelectContent>
          {kind === 'boolean' && <SelectItem value={EMPTY_SELECT}>Choose</SelectItem>}
          {(options ?? [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]).map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : kind === 'textarea' ? (
      <Textarea
        autoFocus
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        onBlur={(event) => commitOnBlur(event.currentTarget.value)}
        onKeyDown={(event) => inlineTextareaKey(event, commit, cancel)}
        disabled={disabled || pending}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={cn(
          'min-h-20 text-sm',
          invalid && 'border-destructive/60 bg-destructive/5 focus-visible:ring-destructive/25',
          inputClassName,
        )}
      />
    ) : (
      <Input
        autoFocus
        type={kind === 'number' || kind === 'money' ? 'number' : kind === 'date' ? 'date' : 'text'}
        inputMode={kind === 'money' || kind === 'number' ? 'decimal' : undefined}
        step={kind === 'money' || kind === 'number' ? '0.01' : undefined}
        min={kind === 'money' || kind === 'number' ? '0' : undefined}
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        onBlur={(event) => commitOnBlur(event.currentTarget.value)}
        onKeyDown={(event) => inlineEditKey(event, commit, cancel)}
        disabled={disabled || pending}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-7 text-sm',
          invalid && 'border-destructive/60 bg-destructive/5 focus-visible:ring-destructive/25',
          align === 'right' && 'text-right',
          inputClassName,
        )}
      />
    )

  // As tall as the value it replaced (min-h-8), so opening a field doesn't nudge the lines below it.
  return (
    <div className={cn('flex min-h-8 items-center', align === 'right' && 'justify-end')}>{editor}</div>
  )
}

function valueToInput(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object') return ''
  return String(value)
}
